import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useContextoSesion } from '../miscelaneo/ContextoSesion';
import qzHidService from '../services/qzHidService';
import { crearAcumuladorBarcode } from '../services/hidBarcodeParser';

const ContextoScanner = createContext();

export const useScanner = () => useContext(ContextoScanner);

export const ContextoScannerProvider = ({ children }) => {
  const { sesionCaja } = useContextoSesion();

  const [scannerConectado, setScannerConectado] = useState(false);
  const [conectandoScanner, setConectandoScanner] = useState(false);
  const [ultimoBarcode, setUltimoBarcode] = useState(null);

  const listenersRef = useRef(new Set());
  const vendorIdRef = useRef(null);
  const productIdRef = useRef(null);

  const habilitado = sesionCaja?.modo_scanner === 'qz_hid'
    && !!sesionCaja?.scanner_vendor_id
    && !!sesionCaja?.scanner_product_id;

  const notificarListeners = useCallback((barcode) => {
    setUltimoBarcode({ codigo: barcode, timestamp: Date.now() });
    listenersRef.current.forEach(fn => {
      try { fn(barcode); } catch (err) {
        console.error('[Scanner] Error en listener:', err);
      }
    });
  }, []);

  const conectarScanner = useCallback(async () => {
    if (!habilitado || scannerConectado || conectandoScanner) return false;

    const vendorId = sesionCaja.scanner_vendor_id;
    const productId = sesionCaja.scanner_product_id;

    vendorIdRef.current = vendorId;
    productIdRef.current = productId;
    setConectandoScanner(true);

    try {
      // Set up HID callback with barcode accumulator
      const acumulador = crearAcumuladorBarcode(notificarListeners);
      qzHidService.setCallbackHid((streamEvent) => {
        if (streamEvent?.type === 'RECEIVE' && streamEvent?.output) {
          acumulador(streamEvent.output);
        } else if (streamEvent?.type === 'ERROR') {
          console.error('[Scanner] Error HID:', streamEvent.exception);
        }
      });

      const claimed = await qzHidService.reclamarDispositivo(vendorId, productId);
      if (!claimed) {
        console.warn('[Scanner] No se pudo reclamar dispositivo', vendorId, productId);
        return false;
      }

      const opened = await qzHidService.abrirStream(vendorId, productId);
      if (!opened) {
        await qzHidService.liberarDispositivo(vendorId, productId);
        console.warn('[Scanner] No se pudo abrir stream');
        return false;
      }

      setScannerConectado(true);
      console.log('[Scanner] Conectado:', vendorId, productId);
      return true;
    } catch (err) {
      console.error('[Scanner] Error al conectar:', err);
      setScannerConectado(false);
      return false;
    } finally {
      setConectandoScanner(false);
    }
  }, [habilitado, scannerConectado, conectandoScanner, sesionCaja, notificarListeners]);

  const desconectarScanner = useCallback(async () => {
    const vid = vendorIdRef.current;
    const pid = productIdRef.current;
    if (!vid || !pid) return;

    try {
      await qzHidService.cerrarStream(vid, pid);
      await qzHidService.liberarDispositivo(vid, pid);
    } catch (err) {
      console.error('[Scanner] Error al desconectar:', err);
    }
    setScannerConectado(false);
    vendorIdRef.current = null;
    productIdRef.current = null;
  }, []);

  // Pub/sub: registrar listener que recibe barcode strings
  const registrarListener = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  // Auto-connect when session has scanner config
  useEffect(() => {
    if (habilitado && !scannerConectado && !conectandoScanner) {
      const timer = setTimeout(() => conectarScanner(), 2500);
      return () => clearTimeout(timer);
    }
    if (!habilitado && scannerConectado) {
      desconectarScanner();
    }
  }, [habilitado, scannerConectado, conectandoScanner, conectarScanner, desconectarScanner]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const vid = vendorIdRef.current;
      const pid = productIdRef.current;
      if (vid && pid) {
        qzHidService.cerrarStream(vid, pid).catch(() => {});
        qzHidService.liberarDispositivo(vid, pid).catch(() => {});
      }
    };
  }, []);

  const value = {
    scannerConectado,
    conectandoScanner,
    habilitado,
    ultimoBarcode,
    registrarListener,
    conectarScanner,
    desconectarScanner,
  };

  return (
    <ContextoScanner.Provider value={value}>
      {children}
    </ContextoScanner.Provider>
  );
};

export default ContextoScanner;
